import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Checkbox, Dialog, TextField, Typogaphy } from 'vnetwork-ui'

export interface ColumnMapping {
    header: string
    data_field: string
    width?: number
    /** @deprecated Use is_required instead */
    is_collaborator?: boolean
    /** If true, column is always included and cannot be deselected */
    is_required?: boolean
    formatter_type?:
        | 'array_join'
        | 'date'
        | 'currency'
        | 'boolean'
        | 'default'
        | 'service_status'
        | 'employee_status'
        | 'department_status'
    formatter_options?: {
        separator?: string
        date_format?: string
        currency_symbol?: string
        true_value?: string
        false_value?: string
        [key: string]: any
    }
}

export interface ActiveFilter {
    label: string
    value: string | string[]
}

interface ExcelExportModalProps {
    open: boolean
    onClose: () => void
    onExport: (columns: ColumnMapping[], filename: string) => void
    availableColumns: ColumnMapping[]
    feature?: string
    /** Active filters to display in modal */
    activeFilters?: ActiveFilter[]
}

const ExcelExportModal: React.FC<ExcelExportModalProps> = ({
    open,
    onClose,
    onExport,
    availableColumns,
    feature = 'Export',
    activeFilters = [],
}) => {
    const { t } = useTranslation()

    // Separate required columns (always included) from optional columns (user can select)
    const requiredColumns = availableColumns.filter((col) => col.is_required)
    const optionalColumns = availableColumns.filter((col) => !col.is_required)

    // Track selected optional columns (default: all selected)
    const [selectedOptionalColumns, setSelectedOptionalColumns] =
        useState<ColumnMapping[]>(optionalColumns)
    const [filename, setFilename] = useState('')

    const handleColumnToggle = (column: ColumnMapping) => {
        setSelectedOptionalColumns((prev) => {
            const exists = prev.find(
                (col) => col.data_field === column.data_field
            )
            if (exists) {
                return prev.filter(
                    (col) => col.data_field !== column.data_field
                )
            } else {
                return [...prev, column]
            }
        })
    }

    const handleSelectAll = () => {
        setSelectedOptionalColumns(optionalColumns)
    }

    const handleDeselectAll = () => {
        setSelectedOptionalColumns([])
    }

    const handleExport = () => {
        // Combine required columns + selected optional columns
        const allSelectedColumns = [
            ...requiredColumns,
            ...selectedOptionalColumns,
        ]
        onExport(allSelectedColumns, filename)
        onClose()
    }

    // Total selected = required + selected optional
    const totalSelected =
        requiredColumns.length + selectedOptionalColumns.length

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={t(`export.${feature}`)}
            maxWidth="md"
            closeOnClick={false}
        >
            <div className="space-y-4">
                {/* Active Filters Display */}
                {activeFilters.length > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                        <Typogaphy className="text-sm font-medium text-blue-800 mb-2">
                            {t('export.active_filters', {
                                defaultValue: 'Bộ lọc đang áp dụng',
                            })}
                        </Typogaphy>
                        <div className="flex flex-wrap gap-2">
                            {activeFilters.map((filter, index) => (
                                <span
                                    key={index}
                                    className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-blue-200 rounded text-xs text-blue-700"
                                >
                                    <span className="font-medium">
                                        {filter.label}:
                                    </span>
                                    <span>
                                        {Array.isArray(filter.value)
                                            ? filter.value.join(', ')
                                            : filter.value}
                                    </span>
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                <div>
                    <Typogaphy className="mb-2">{t('name_of_file')}</Typogaphy>
                    <TextField
                        size="small"
                        placeholder={t('export.enter_file_name')}
                        value={filename}
                        onChange={(e) => setFilename(e.target.value)}
                    />
                </div>
                <div>
                    <div className="flex justify-between items-center mb-2">
                        <Typogaphy>
                            {t('export.choose_columns', {
                                total: availableColumns?.length,
                                selecteds: totalSelected,
                            })}
                        </Typogaphy>
                        {optionalColumns.length > 0 && (
                            <div className="space-x-2 flex">
                                <Button
                                    size="small"
                                    variant="text"
                                    onClick={handleSelectAll}
                                >
                                    {t('select_all')}
                                </Button>
                                <Button
                                    size="small"
                                    variant="text"
                                    onClick={handleDeselectAll}
                                >
                                    {t('deselect_all')}
                                </Button>
                            </div>
                        )}
                    </div>

                    <div className="max-h-64 overflow-y-auto border rounded p-2 space-y-1">
                        {/* Required columns - always included, no checkbox */}
                        {requiredColumns.map((column) => (
                            <div
                                key={column.data_field}
                                className="flex items-center space-x-2 p-1 rounded bg-gray-50"
                            >
                                <Checkbox
                                    checked={true}
                                    disabled
                                    size="small"
                                />
                                <div className="flex-1">
                                    <Typogaphy className="text-gray-600">
                                        {t(column?.header)}
                                    </Typogaphy>
                                </div>
                            </div>
                        ))}

                        {/* Optional columns - user can select/deselect */}
                        {optionalColumns.map((column) => {
                            const isSelected = selectedOptionalColumns.some(
                                (col) => col.data_field === column.data_field
                            )
                            return (
                                <div
                                    key={column.data_field}
                                    className="flex items-center space-x-2 hover:bg-gray-50 p-1 rounded cursor-pointer"
                                    onClick={() => handleColumnToggle(column)}
                                >
                                    <Checkbox
                                        checked={isSelected}
                                        onChange={() =>
                                            handleColumnToggle(column)
                                        }
                                        size="small"
                                    />
                                    <div className="flex-1">
                                        <Typogaphy>
                                            {t(column?.header)}
                                        </Typogaphy>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>

                <div className="flex justify-end space-x-2 pt-4">
                    <Button variant="outlined" onClick={onClose}>
                        {t('cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleExport}
                        disabled={totalSelected === 0}
                    >
                        {t('export_excel')} ({totalSelected} {t('columns')})
                    </Button>
                </div>
            </div>
        </Dialog>
    )
}

export default ExcelExportModal
